import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useAuthTranslation, useValidationTranslation } from '@/i18n/hooks/useTypedTranslation';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { LoginDto } from '@/types';

export function Login() {
  const { login } = useAuth();
  const { success } = useToast();
  const navigate = useNavigate();
  const { t } = useAuthTranslation();
  const { t: tValidation } = useValidationTranslation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginDto>();

  const onSubmit = async (data: LoginDto) => {
    setLoading(true);
    setError(null);
    try {
      await login(data);
      success(t('login.title'));
      navigate('/dashboard');
    } catch (err) {
      // Extract error message from API response
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || t('login.errors.invalidCredentials');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
          }}
        >
          {t('login.title')}
        </Typography>
      </Box>

      {/* Form Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 3.5 },
          borderRadius: { xs: 3, sm: 4 },
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgb(20 184 166 / 0.08)',
            borderColor: (theme) => theme.palette.mode === 'light'
              ? 'rgba(20, 184, 166, 0.15)'
              : 'rgba(20, 184, 166, 0.3)',
          },
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t('login.email.label')}
            type="email"
            placeholder={t('login.email.placeholder')}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            {...register('email', {
              required: tValidation('email.required'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: tValidation('email.invalid'),
              },
            })}
          />

          <TextField
            fullWidth
            label={t('login.password.label')}
            type={showPassword ? 'text' : 'password'}
            placeholder={t('login.password.placeholder')}
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <VisibilityOffIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...register('password', { required: tValidation('password.required') })}
          />

          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Typography
              component={Link}
              to="/forgot-password"
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main',
                },
              }}
            >
              {t('login.forgotPassword')}
            </Typography>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '10px',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(20, 184, 166, 0.35)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                {t('login.submitting')}
              </>
            ) : (
              t('login.submit')
            )}
          </Button>
        </Box>
      </Paper>

      {/* Sign Up Link */}
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
        {t('login.noAccount')}{' '}
        <Typography
          component={Link}
          to="/register"
          variant="body2"
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            fontWeight: 600,
            transition: 'color 0.2s ease',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.dark',
            },
          }}
        >
          {t('login.createAccount')}
        </Typography>
      </Typography>
    </Box>
  );
}

export default Login;
